import React from "react";
import EntityForm from "../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../Common/utils";
import {
  AVAILABILITY,
  BLOCKAGE,
  EXTRA_TYPE_VARIANT,
  GUEST_TYPE,
  RESTRICTIONS_TYPE,
  StayDaysFilterType,
} from "../../Common/constants";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import "./customStyles.css";
import { withPropertyData } from "../../../../components/property-admin-data-provider";
import multimediaTransform from "../../../../lib/multimediaTransform";
import {
  EXTRA_TYPES_WITH_AVAILABILITY,
  EXTRA_TYPES_WITH_STAY_DAYS_FILTER,
} from "./ModuleDefinition";

function customEntityToFormValuesMapper({ entity, fields, locales, translationConfig }) {
  let formValues = entityToFormValuesMapper({
    entity,
    fields,
    locales,
    translationConfig,
  });
  const rateType = entity.rate && entity.rate.type;

  let type = entity.variant
    ? entity.type.concat(`_${entity.variant}`)
    : entity.type || EXTRA_TYPE_VARIANT.ROOM;

  const rateAmount = entity.rate && entity.rate.value && entity.rate.value;
  const promoCode =
    entity.restrictions &&
    entity.restrictions.code &&
    entity.restrictions.code.value.value.join(",");
  const valid = keyword =>
    entity.restrictions &&
    entity.restrictions.travelWindow.value.value &&
    entity.restrictions.travelWindow.value.value.period[0].dateRange[keyword]
      ? moment(
          entity.restrictions.travelWindow.value.value.period[0].dateRange[keyword],
          "YYYY-MM-DD"
        )
      : undefined;

  const returnValueIfTypeConstant = keyword =>
    entity.restrictions &&
    entity.restrictions[keyword] &&
    entity.restrictions[keyword].value.type === RESTRICTIONS_TYPE.CONSTANT
      ? entity.restrictions[keyword].value.value
      : "";
  const active = entity.active === undefined ? true : entity.active;
  const validFrom = valid("start") || new Date();
  const defaultValidTo = new Date();
  defaultValidTo.setFullYear(defaultValidTo.getFullYear() + 10);
  const validTo = valid("end") || defaultValidTo;
  const appliesToGuestsDefault = [
    GUEST_TYPE.ADULT,
    GUEST_TYPE.CHILD,
    GUEST_TYPE.BABY,
    GUEST_TYPE.TEENAGER,
    GUEST_TYPE.SENIOR,
  ];
  const appliesToGuests =
    entity.appliesToGuests === undefined || entity.appliesToGuests.length === 0
      ? appliesToGuestsDefault
      : entity.appliesToGuests;
  const maxBookableUnits = entity.maxBookableUnits === undefined ? 1 : entity.maxBookableUnits;
  const minStayType =
    (entity.restrictions &&
      entity.restrictions.minStay &&
      entity.restrictions.minStay.value.type) ||
    RESTRICTIONS_TYPE.NO_LIMIT;
  const maxStayType =
    (entity.restrictions &&
      entity.restrictions.maxStay &&
      entity.restrictions.maxStay.value.type) ||
    RESTRICTIONS_TYPE.NO_LIMIT;
  const minNoticeType =
    (entity.restrictions &&
      entity.restrictions.minNotice &&
      entity.restrictions.minNotice.value.type) ||
    RESTRICTIONS_TYPE.NO_LIMIT;
  const maxNoticeType =
    (entity.restrictions &&
      entity.restrictions.maxNotice &&
      entity.restrictions.maxNotice.value.type) ||
    RESTRICTIONS_TYPE.NO_LIMIT;
  const minStayValue = Number(returnValueIfTypeConstant("minStay")) / 24;
  const maxStayValue = Number(returnValueIfTypeConstant("maxStay")) / 24;
  const minNoticeValue = returnValueIfTypeConstant("minNotice");
  const maxNoticeValue = returnValueIfTypeConstant("maxNotice");
  const availabilityType =
    entity.restrictions &&
    entity.restrictions.availability &&
    `AVAILABILITY_${entity.restrictions.availability.value.type}`;
  const availabilityValue =
    entity.restrictions &&
    entity.restrictions.availability &&
    entity.restrictions.availability.value.type === AVAILABILITY.SHARED &&
    entity.restrictions.availability.value.value;
  const blockingType =
    entity.restrictions && entity.restrictions.blockage && entity.restrictions.blockage.value.type;
  const blockingValue =
    entity.restrictions &&
    entity.restrictions.blockage &&
    entity.restrictions.blockage.value.type === BLOCKAGE.SHARED &&
    entity.restrictions.blockage.value.value;

  let originalMultimedia = [];

  if (entity.multimedia) {
    originalMultimedia = multimediaTransform(entity.multimedia);
  }

  const multimedia = originalMultimedia || [];
  const appliesTo = (entity.appliesTo && entity.appliesTo[0].tickers) || [];

  return {
    ...formValues,
    ...entity.properties,
    active,
    type,
    rateType,
    rateAmount,
    promoCode,
    validFrom,
    validTo,
    minStayType,
    maxStayType,
    minNoticeType,
    maxNoticeType,
    minStayValue,
    maxStayValue,
    minNoticeValue,
    maxNoticeValue,
    originalMultimedia,
    availabilityType,
    availabilityValue,
    blockingType,
    blockingValue,
    appliesToGuests,
    maxBookableUnits,
    appliesTo,
    multimedia,
  };
}

class ExtrasForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      error: null,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }
  getFields(fields) {
    return fields && fields.form;
  }

  async handleSubmit({ formValues, entity }) {
    const splitType = formValues.type.includes("_")
      ? formValues.type.split("_")
      : Array(formValues.type);
    const type = splitType[0];
    const variant = splitType[3]
      ? `${splitType[1]}_${splitType[2]}_${splitType[3]}`
      : splitType[2]
      ? `${splitType[1]}_${splitType[2]}`
      : splitType[1] || null;
    const id = entity.id ? entity.id : uuidv4();
    const ticker = entity.ticker || `e_${formValues.ticker.replace(/_/g, "")}`.slice(0, 10);
    const rate = { type: formValues.rateType, value: formValues.rateAmount };
    const active = formValues.active;
    const appliesTo = [{ type: "INVENTORY", tickers: formValues.appliesTo }];
    const appliesToGuests = formValues.appliesToGuests;
    const maxBookableUnits = Number(formValues.maxBookableUnits);
    const notificationEmails = Array.isArray(formValues.notificationEmails)
      ? formValues.notificationEmails
      : typeof formValues.notificationEmails === "string" && formValues.notificationEmails
      ? formValues.notificationEmails.split(",").map(e => e.trim())
      : [];
    const translations = formValues.translations;
    const restrictions = {
      blockage: {
        type: "BLOCKAGE",
        value: {
          type: formValues.blockingType,
          value: formValues.blockingValue,
        },
      },
      minStay: {
        type: "MIN_STAY",
        value: {
          type: formValues.minStayType,
          value: formValues.minStayValue * 24,
        },
      },
      maxStay: {
        type: "MAX_STAY",
        value: {
          type: formValues.maxStayType,
          value: formValues.maxStayValue * 24,
        },
      },
      minNotice: {
        type: "MIN_NOTICE",
        value: {
          type: formValues.minNoticeType,
          value: Number(formValues.minNoticeValue),
        },
      },
      maxNotice: {
        type: "MAX_NOTICE",
        value: {
          type: formValues.maxNoticeType,
          value: Number(formValues.maxNoticeValue),
        },
      },
      code: {
        type: "CODE",
        value: {
          type: "CONSTANT",
          value: formValues.promoCode ? formValues.promoCode.split(",").map(e => e.trim()) : [],
        },
      },
      travelWindow: {
        type: "TRAVEL_WINDOW",
        value: {
          type: "CONSTANT",
          value: {
            isOpen: true,
            period: {
              days: [],
              dateRange: {
                start: moment(formValues.validFrom).utc(true).toISOString(),
                end: moment(formValues.validTo).utc(true).toISOString(),
              },
            },
          },
        },
      },
    };
    if (EXTRA_TYPES_WITH_AVAILABILITY.includes(formValues.type) && formValues.availabilityType) {
      restrictions.availability = {
        type: "AVAILABILITY",
        value: {
          type: formValues.availabilityType.split("_")[1],
          value: formValues.availabilityValue,
        },
      };
    }
    // FIXME: wtf, set these only if they have type/value
    if (!restrictions.blockage.value.type) {
      delete restrictions.blockage;
    }
    if (!restrictions.code.value.value) {
      delete restrictions.code;
    }
    if (restrictions.minStay.value.type === RESTRICTIONS_TYPE.NO_LIMIT) {
      delete restrictions.minStay;
    }
    if (restrictions.maxStay.value.type === RESTRICTIONS_TYPE.NO_LIMIT) {
      delete restrictions.maxStay;
    }
    if (restrictions.minNotice.value.type === RESTRICTIONS_TYPE.NO_LIMIT) {
      delete restrictions.minNotice;
    }
    if (restrictions.maxNotice.value.type === RESTRICTIONS_TYPE.NO_LIMIT) {
      delete restrictions.maxNotice;
    }
    const stayDaysFilterType = EXTRA_TYPES_WITH_STAY_DAYS_FILTER.includes(formValues.type)
      ? formValues.stayDaysFilterType
      : StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKOUT;
    const entityToSend = {
      type,
      variant,
      id,
      ticker,
      rate,
      active,
      appliesTo,
      appliesToGuests,
      maxBookableUnits,
      notificationEmails,
      translations,
      restrictions,
      lockType: formValues.lockType,
      stayDaysFilterType,
    };

    // TODO move extrasData creation to its own function
    const getPayOptions = () => {
      switch (formValues.type) {
        case EXTRA_TYPE_VARIANT.ROOM:
          return 0;
        case EXTRA_TYPE_VARIANT.ROOM_MANDATORY:
          return 6;
        case EXTRA_TYPE_VARIANT.PERSON:
          return 1;
        case EXTRA_TYPE_VARIANT.PERSON_MANDATORY:
          return 7;
        case EXTRA_TYPE_VARIANT.ROOM_PER_DAY:
          return 2;
        case EXTRA_TYPE_VARIANT.ROOM_PER_DAY_MANDATORY:
          return 8;
        case EXTRA_TYPE_VARIANT.PERSON_PER_DAY:
          return 3;
        case EXTRA_TYPE_VARIANT.PERSON_PER_DAY_MANDATORY:
          return 9;
        case EXTRA_TYPE_VARIANT.UNIT:
          return 4;
        case EXTRA_TYPE_VARIANT.UNIT_PER_DAY:
          return 5;
        default:
          return 4;
      }
    };

    async function requestQueueAfterMain(response, sendRequest) {
      const propertyTicker = entity.propertyTicker || entity.hotelTicker;
      const requestQueueArray = [];

      formValues.originalMultimedia.forEach(original => {
        if (
          // If none of the new ones includes old id
          !formValues.multimedia.some(modified => modified.id === original.id)
        ) {
          requestQueueArray.push({
            url: `/v1/${propertyTicker}/extra/${response.data.id}/media/${original.id}`,
            method: "DELETE",
          });
        }
      });

      formValues.multimedia.forEach(modified => {
        const translations =
          (modified.translations &&
            Object.keys(modified.translations).map(it => ({
              locale: it,
              ...modified.translations[it],
            }))) ||
          [];

        if (!formValues.originalMultimedia.some(original => original.id === modified.id)) {
          const data = new FormData();
          data.append("image", modified);
          data.append("translations", JSON.stringify(translations));
          data.append("id", modified.id);
          requestQueueArray.push({
            url: `/v1/${propertyTicker}/extra/${response.data.id}/media/`,
            data: data,
            method: "POST",
          });
        }
        if (
          formValues.originalMultimedia.some(
            original =>
              original.id === modified.id &&
              JSON.stringify(modified.translations) !== JSON.stringify(original.translations)
          )
        ) {
          requestQueueArray.push({
            url: `/v1/${propertyTicker}/extra/${response.data.id}/media/${modified.id}`,
            data: {
              id: modified.id,
              filename: modified.modifiedName,
              url: modified.preview,
              translations: translations,
            },
            method: "PUT",
          });
        }
      });

      if (
        formValues.multimedia &&
        formValues.multimedia.length > 1 &&
        formValues.originalMultimedia &&
        formValues.originalMultimedia.length > 0 &&
        formValues.multimedia.find(
          (it, idx) =>
            it.id !== (formValues.originalMultimedia[idx] && formValues.originalMultimedia[idx].id)
        )
      ) {
        requestQueueArray.push({
          url: `/v1/${propertyTicker}/extra/${response.data.id}/media/reorder`,
          data: formValues.multimedia.map(it => it.id),
          method: "PUT",
        });
      }

      for (let [idx, request] of requestQueueArray.entries()) {
        await sendRequest({
          options: request,
          reloadEntities: false,
          // Resets cache after completing all operations
          resetCache: idx + 1 === requestQueueArray.length,
        });
      }
    }

    this.props.onSubmitEntity({
      entity: entityToSend,
      requestQueueAfterMain,
    });
  }

  render() {
    const {
      name,
      validation,
      fields,
      renderers,
      translation,
      entities,
      tabs,
      showMenu,
      allowImageEdit,
    } = this.props.config;
    const { locales, entity, entityError, entityResult, loading, getListPath } = this.props;

    const { getFields, handleSubmit } = this;

    const concatEntity = {
      ...entity,
      blockingValues: this.props.wbkData ? this.props.wbkData.blockingInventoryTickers : [],
      inventories: this.props.wbkData ? this.props.wbkData.inventories : [],
    };
    return (
      <div className={"wbkv9-extras-form"}>
        <EntityForm
          loading={loading}
          name={name}
          entity={concatEntity}
          entities={entities}
          validation={validation}
          renderers={renderers}
          fields={getFields(fields)}
          locales={locales}
          translation={translation}
          onSubmit={handleSubmit}
          entityError={entityError || this.state.error}
          entityResult={entityResult}
          getListPath={getListPath}
          entityToFormValuesMapper={customEntityToFormValuesMapper}
          validateVisibleOnly
          tabs={tabs}
          showMenu={showMenu}
          allowImageEdit={allowImageEdit}
        />
      </div>
    );
  }
}

ExtrasForm.propTypes = {};

ExtrasForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
  getListPath: function getListPath({ hotelTicker, entityName }) {
    return `/${hotelTicker}/${entityName}/`;
  },
};

export default withPropertyData(ExtrasForm);
